import { useState } from "react";
import { useTranslation } from "react-i18next";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Check, ChevronDown } from "lucide-react";
import type { Variants } from "motion/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu/index.js";
import { SimpleTooltip } from "../../ui/tooltip/index.js";
import {
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from "../../../i18n/index.js";
import { AnimatePresence, motion, useIsPresent, useReducedMotion } from "../../../lib/motion.js";
import { cn } from "../../../lib/utils.js";
import { RAIL_COMPACT_HIT, RAIL_FOCUS, railCompactRowClassName } from "../../layout/rail-compact.js";

/** `entry` is the FeedBot auth/onboarding footer selector (Login, Chooser, Invitation). */
export type LanguageSwitcherVariant = "default" | "entry" | "rail" | "rail-collapsed";

interface LanguageSwitcherProps {
  variant?: LanguageSwitcherVariant;
}

/** Native regional-indicator flag glyphs — no SVG paths, no flag package. */
const LANGUAGE_FLAGS: Record<SupportedLanguage, string> = {
  he: "🇮🇱",
  en: "🇺🇸",
};

export function LanguageFlag({
  lang,
  className,
}: {
  lang: SupportedLanguage;
  className?: string;
}): React.JSX.Element {
  return (
    <span
      className={cn(
        "inline-flex size-5 shrink-0 items-center justify-center text-[1.05rem] leading-none",
        className,
      )}
      aria-hidden="true"
    >
      {LANGUAGE_FLAGS[lang]}
    </span>
  );
}

/** FeedBot language row shell, shared by the entry selector and the account menu. */
export function languageOptionRowClassName(selected: boolean): string {
  return cn(
    "flex w-full min-w-0 cursor-pointer items-center gap-3 rounded-[10px] border-0 px-3 py-2 text-start transition-colors duration-150",
    selected
      ? "bg-brand-mint hover:bg-[#dcf1e8] data-[highlighted]:bg-[#dcf1e8]"
      : "bg-transparent hover:bg-[#f3f8f6] active:bg-[#f3f8f6] data-[highlighted]:bg-[#f3f8f6]",
  );
}

/** Flag, native name, translated subtitle, and selection check. */
export function LanguageOptionContent({
  lang,
  selected,
}: {
  lang: SupportedLanguage;
  selected: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <LanguageFlag lang={lang} className="text-[1.15rem]" />
      <span className="grid min-w-0 flex-1 text-start">
        <span className="truncate text-[14px] leading-5 font-[700] text-[#17302e]">
          <bdi lang={lang}>{t(`language.${lang}`)}</bdi>
        </span>
        <span className="truncate text-[12px] leading-4 font-[500] text-[#82958b]">
          <bdi>{t(`language.subtitle.${lang}`)}</bdi>
        </span>
      </span>
      <Check
        size={16}
        strokeWidth={2.5}
        className={cn(
          "shrink-0 text-brand-primary transition-opacity duration-150",
          selected ? "opacity-100" : "opacity-0",
        )}
        aria-hidden="true"
      />
    </>
  );
}

function LanguageMenuItems({
  current,
  onSelect,
}: {
  current: SupportedLanguage;
  onSelect: (lang: SupportedLanguage) => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <>
      {SUPPORTED_LANGUAGES.map((lang) => {
        const selected = lang === current;
        const rowDir = lang === "he" ? "rtl" : "ltr";
        return (
          <DropdownMenuItem
            key={lang}
            className="rounded-md px-2 py-1.5"
            onSelect={() => onSelect(lang)}
            data-testid={`language-option-${lang}`}
          >
            <span
              dir={rowDir}
              className="flex w-full min-w-0 items-center gap-2.5"
            >
              <LanguageFlag lang={lang} />
              <span className="min-w-0 flex-1 text-start text-[0.8125rem] font-medium leading-5">
                {t(`language.${lang}`)}
              </span>
              <span
                className={cn(
                  "inline-flex size-4 shrink-0 items-center justify-center text-accent",
                  selected ? "opacity-100" : "opacity-0",
                )}
              >
                <Check size={14} aria-hidden="true" />
              </span>
            </span>
          </DropdownMenuItem>
        );
      })}
    </>
  );
}

/** Rail variants: the FeedBot language rows in a white popover that opens above the rail footer. */
function RailLanguageMenu({
  current,
  compact,
  onSelect,
}: {
  current: SupportedLanguage;
  compact: boolean;
  onSelect: (lang: SupportedLanguage) => void;
}): React.JSX.Element {
  return (
    <DropdownMenuContent
      side="top"
      align={compact ? "center" : "start"}
      sideOffset={8}
      collisionPadding={12}
      className="font-feedbot grid w-[max(var(--radix-dropdown-menu-trigger-width),13.5rem)] gap-1 rounded-[14px] border-[#dce7df] bg-white p-1.5 text-[#17302e] shadow-[0_24px_60px_#06251d2e]"
      data-testid="language-menu"
    >
      {SUPPORTED_LANGUAGES.map((lang) => {
        const selected = lang === current;
        return (
          <DropdownMenuItem
            key={lang}
            className={languageOptionRowClassName(selected)}
            onSelect={() => onSelect(lang)}
            data-testid={`language-option-${lang}`}
          >
            <LanguageOptionContent lang={lang} selected={selected} />
          </DropdownMenuItem>
        );
      })}
    </DropdownMenuContent>
  );
}

function entryMenuMotion(reduceMotion: boolean): Variants {
  if (reduceMotion) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1, transition: { duration: 0 } },
      exit: { opacity: 0, transition: { duration: 0 } },
    };
  }
  return {
    initial: { opacity: 0, scale: 0.97, y: 6 },
    animate: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] },
    },
    exit: {
      opacity: 0,
      scale: 0.97,
      y: 4,
      transition: { duration: 0.15, ease: [0.4, 0, 0.6, 1] },
    },
  };
}

/**
 * Direct child of `AnimatePresence`: Radix stays force-mounted through the
 * exit variant, and the menu is inert while it animates out.
 */
function EntryLanguageMenu({
  current,
  onSelect,
}: {
  current: SupportedLanguage;
  onSelect: (lang: SupportedLanguage) => void;
}): React.JSX.Element {
  const { i18n } = useTranslation();
  const isPresent = useIsPresent();
  const reduceMotion = useReducedMotion() ?? false;

  return (
    <DropdownMenuPrimitive.Portal forceMount>
      <DropdownMenuPrimitive.Content
        forceMount
        asChild
        side="bottom"
        align="center"
        sideOffset={8}
        collisionPadding={12}
        style={{ direction: i18n.dir() === "rtl" ? "rtl" : "ltr" }}
        data-testid="language-menu"
      >
        <motion.div
          className="font-feedbot z-[var(--z-popover)] grid w-[var(--radix-dropdown-menu-trigger-width)] min-w-52 gap-1 rounded-[14px] border border-[#dce7df] bg-white p-1.5 text-start shadow-[0_18px_50px_#1c503017] outline-none"
          style={{ transformOrigin: "var(--radix-dropdown-menu-content-transform-origin)" }}
          variants={entryMenuMotion(reduceMotion)}
          initial="initial"
          animate="animate"
          exit="exit"
          inert={!isPresent}
        >
          {SUPPORTED_LANGUAGES.map((lang) => {
            const selected = lang === current;
            return (
              <DropdownMenuItem
                key={lang}
                className={languageOptionRowClassName(selected)}
                onSelect={() => onSelect(lang)}
                data-testid={`language-option-${lang}`}
              >
                <LanguageOptionContent lang={lang} selected={selected} />
              </DropdownMenuItem>
            );
          })}
        </motion.div>
      </DropdownMenuPrimitive.Content>
    </DropdownMenuPrimitive.Portal>
  );
}

function EntryLanguageSwitcher({
  current,
  label,
  currentLabel,
  onSelect,
}: {
  current: SupportedLanguage;
  label: string;
  currentLabel: string;
  onSelect: (lang: SupportedLanguage) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="group font-feedbot inline-flex h-10 w-[12.5rem] min-w-0 items-center gap-2.5 rounded-[12px] border border-[#b9dccf] bg-white ps-3 pe-2.5 text-start text-[#17302e] shadow-[0_2px_8px_#1c50300a] transition-[border-color,box-shadow] duration-150 ease-in-out hover:border-[#7cc5ae] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand/35 data-[state=open]:border-[#7cc5ae] data-[state=open]:shadow-[0_0_0_3px_#55bca61f]"
          aria-label={`${label}: ${currentLabel}`}
          data-testid="language-trigger-entry"
        >
          <LanguageFlag lang={current} className="text-[1.15rem]" />
          <span className="min-w-0 flex-1 truncate text-[14px] font-[700]">
            <bdi lang={current}>{currentLabel}</bdi>
          </span>
          <ChevronDown
            size={16}
            className="shrink-0 text-[#82958b] transition-transform duration-200 ease-out group-data-[state=open]:rotate-180 motion-reduce:transition-none"
            aria-hidden="true"
          />
        </button>
      </DropdownMenuTrigger>
      <AnimatePresence>
        {open ? <EntryLanguageMenu key="language-menu" current={current} onSelect={onSelect} /> : null}
      </AnimatePresence>
    </DropdownMenu>
  );
}

export function LanguageSwitcher({
  variant = "default",
}: LanguageSwitcherProps): React.JSX.Element {
  const { t, i18n } = useTranslation();

  const current = (SUPPORTED_LANGUAGES as readonly string[]).includes(i18n.language)
    ? (i18n.language as SupportedLanguage)
    : "he";

  const label = t("language.label");
  const currentLabel = t(`language.${current}`);

  function handleChange(value: SupportedLanguage): void {
    void i18n.changeLanguage(value);
  }

  const menu = (
    <DropdownMenuContent
      align="start"
      side="top"
      className="w-52 rounded-xl border-line p-1.5 shadow-lg"
      data-testid="language-menu"
    >
      <LanguageMenuItems current={current} onSelect={handleChange} />
    </DropdownMenuContent>
  );

  if (variant === "entry") {
    return (
      <EntryLanguageSwitcher
        current={current}
        label={label}
        currentLabel={currentLabel}
        onSelect={handleChange}
      />
    );
  }

  if (variant === "rail-collapsed") {
    return (
      <div className={railCompactRowClassName()}>
        <DropdownMenu>
          <SimpleTooltip content={currentLabel}>
            <div>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={RAIL_COMPACT_HIT}
                  aria-label={`${label}: ${currentLabel}`}
                  data-testid="language-trigger-collapsed"
                >
                  <LanguageFlag lang={current} className="text-[1.15rem]" />
                </button>
              </DropdownMenuTrigger>
            </div>
          </SimpleTooltip>
          <RailLanguageMenu current={current} compact onSelect={handleChange} />
        </DropdownMenu>
      </div>
    );
  }

  if (variant === "rail") {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              "group flex h-10 w-full min-w-0 items-center gap-2.5 rounded-[10px] border border-white/[0.08] bg-white/[0.04] ps-2.5 pe-2 text-start text-[#eef6f3] transition-[background-color,border-color] duration-150 ease-out hover:border-white/[0.14] hover:bg-white/[0.07] data-[state=open]:border-[rgba(108,225,180,0.3)] data-[state=open]:bg-white/[0.07] motion-reduce:transition-none",
              RAIL_FOCUS,
            )}
            aria-label={`${label}: ${currentLabel}`}
            data-testid="language-trigger-rail"
          >
            <LanguageFlag lang={current} className="text-[1.1rem]" />
            <span className="min-w-0 flex-1 truncate text-[13px] leading-5 font-[650]">
              <bdi lang={current}>{currentLabel}</bdi>
            </span>
            <ChevronDown
              size={15}
              strokeWidth={1.8}
              className="shrink-0 text-[#8fa9a3] transition-transform duration-150 ease-out group-data-[state=open]:rotate-180 motion-reduce:transition-none"
              aria-hidden="true"
            />
          </button>
        </DropdownMenuTrigger>
        <RailLanguageMenu current={current} compact={false} onSelect={handleChange} />
      </DropdownMenu>
    );
  }

  return (
    <label className="inline-flex items-center gap-2">
      <span className="text-[0.8rem] tracking-[0.04em] text-subtle uppercase">{label}</span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-md border border-border bg-surface px-[0.65rem] py-[0.4rem] text-start text-[0.9rem] text-foreground"
            aria-label={label}
            data-testid="language-trigger-default"
          >
            <LanguageFlag lang={current} />
            <span>{currentLabel}</span>
            <ChevronDown size={16} className="opacity-60" aria-hidden="true" />
          </button>
        </DropdownMenuTrigger>
        {menu}
      </DropdownMenu>
    </label>
  );
}
